import React, { useEffect, useMemo, useRef, useState } from "react";
import TitleButtons from "../../../../../components/TitleButtons";
import TextField from "../../../../../components/TextField";
import Button from "../../../../../components/Button";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";

/** =================== Config/API =================== */
const API_BASE = "https://api.eva-googlemarine.com";
const API_URL = `${API_BASE}/departamentos/recursos-humanos/registrar-colaborador`;

const DEFAULT_UPSERT = false;
const REQUEST_TIMEOUT_MS = 8000;

/** =================== Helpers/Máscaras =================== */
const onlyDigits = (s: unknown) => String(s ?? "").replace(/\D+/g, "");

function maskCPF(s: string): string {
  const d = onlyDigits(s).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return d.replace(/^(\d{3})(\d+)/, "$1.$2");
  if (d.length <= 9) return d.replace(/^(\d{3})(\d{3})(\d+)/, "$1.$2.$3");
  return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{0,2}).*/, "$1.$2.$3-$4");
}

function maskBRDate(input: string): string {
  const d = onlyDigits(input).slice(0, 8); // ddmmyyyy
  if (d.length <= 2) return d;
  if (d.length <= 4) return d.replace(/^(\d{2})(\d+)/, "$1/$2");
  return d.replace(/^(\d{2})(\d{2})(\d+)/, "$1/$2/$3");
}

function brToPgDate(input: string): string | null {
  const s = (input || "").trim();
  if (!s) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;

  const [, ddStr, mmStr, yyyyStr] = m;
  const dd = Number(ddStr);
  const mm = Number(mmStr);
  const yyyy = Number(yyyyStr);

  if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(yyyy))
    return null;
  if (yyyy < 1900 || yyyy > 2100) return null;
  if (mm < 1 || mm > 12) return null;

  const maxDay = new Date(Date.UTC(yyyy, mm, 0)).getUTCDate();
  if (dd < 1 || dd > maxDay) return null;

  return `${yyyyStr}-${mmStr}-${ddStr}`;
}

function isValidEmail(x: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x);
}

/** =================== Auth helpers (MESMA LÓGICA) =================== */
function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("access_token");
  } catch {
    return null;
  }
}

function buildAuthHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getEvaAuthUid(): number | null {
  const raw =
    localStorage.getItem("eva_auth_uid") ||
    sessionStorage.getItem("eva_auth_uid") ||
    null;

  if (!raw) return null;
  const s = String(raw).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function buildUidHeaders(): Record<string, string> {
  const uid = getEvaAuthUid();
  if (uid == null) return {};
  return {
    "x-user-id": String(uid),
    "x-eva-auth-uid": String(uid),
  };
}

/** =================== Banner (erro/aviso) =================== */
const NoticeBanner: React.FC<{
  notice: { type: "success" | "error" | "notify"; text: string };
  onClose?: () => void;
  sticky?: boolean;
}> = ({ notice, onClose, sticky = false }) => {
  let bg: string, color: string, border: string;
  switch (notice.type) {
    case "success":
      bg = "#e6ffed";
      color = "#0f5132";
      border = "#badbcc";
      break;
    case "error":
      bg = "#ffecec";
      color = "#842029";
      border = "#f5c2c7";
      break;
    default:
      bg = "#e7f1ff";
      color = "#084298";
      border = "#b6d4fe";
      break;
  }
  return (
    <motion.div
      role="alert"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
      style={{
        margin: "12px 0",
        padding: "10px 12px",
        borderRadius: 8,
        background: bg,
        color,
        border: `1px solid ${border}`,
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1 }}>{notice.text}</span>
        {onClose && (
          <button
            type="button"
            aria-label="Fechar aviso"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        )}
      </div>
    </motion.div>
  );
};

/** =================== Animações =================== */
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.06,
      delayChildren: 0.02,
    },
  },
};
const itemVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 320, damping: 24 },
  },
};

/** =================== Tipos registrar =================== */
type ApiResponseOk = {
  ok: true;
  criado?: boolean;
  editado?: boolean;
  data?: any;
};
type ApiResponseErr = {
  ok: false;
  erro?: string;
  erros?: string[];
  detalhe?: string;
  message?: string;
};
type ApiResponse = ApiResponseOk | ApiResponseErr;

async function safeJson(res: Response) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/** =================== Página =================== */
const RegistrarColaborador: React.FC = () => {
  // Obrigatórios
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [cpfView, setCpfView] = useState("");

  // Opcionais
  const [dataNasc, setDataNasc] = useState("");
  const [setor, setSetor] = useState("");
  const [cargo, setCargo] = useState("");
  const [salario, setSalario] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [contatoEmerg, setContatoEmerg] = useState("");
  const [validadeContrato, setValidadeContrato] = useState("");

  const [upsert] = useState<boolean>(DEFAULT_UPSERT);

  // UI
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<null | {
    type: "success" | "error" | "notify";
    text: string;
  }>(null);

  // ✅ token em tempo real (não só na 1ª render)
  const [tokenNow, setTokenNow] = useState<string | null>(() =>
    getAccessToken()
  );
  const hasAuthToken = useMemo(() => !!tokenNow, [tokenNow]);

  const evaUid = useMemo(() => getEvaAuthUid(), []);

  // ✅ atualiza token se mudar no storage (ex: login em outra aba)
  useEffect(() => {
    const onStorage = () => setTokenNow(getAccessToken());
    window.addEventListener("storage", onStorage);

    // também faz um "pollzinho" leve enquanto a página estiver aberta
    // (porque storage event não dispara na mesma aba que setou)
    const t = setInterval(() => {
      const tkn = getAccessToken();
      setTokenNow((prev) => (prev !== tkn ? tkn : prev));
    }, 1500);

    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(t);
    };
  }, []);

  // abort controller persistente pra evitar setState depois de unmount
  const abortRef = useRef<AbortController | null>(null);

  function clearAll() {
    setNomeCompleto("");
    setCpfView("");
    setDataNasc("");
    setSetor("");
    setCargo("");
    setSalario("");
    setEmail("");
    setTelefone("");
    setContatoEmerg("");
    setValidadeContrato("");
  }

  async function handleRegister() {
    setNotice(null);

    const cpfDigits = onlyDigits(cpfView);
    const erros: string[] = [];

    if (!nomeCompleto.trim()) erros.push("Informe o Nome Completo.");
    if (!cpfDigits || cpfDigits.length !== 11)
      erros.push("CPF inválido (11 dígitos).");
    if (email.trim() && !isValidEmail(email.trim()))
      erros.push("E-mail inválido.");

    const pgNasc = brToPgDate(dataNasc);
    const pgValidade = brToPgDate(validadeContrato);

    if (dataNasc.trim() && !pgNasc)
      erros.push("Data de Nascimento inválida. Use DD/MM/AAAA.");
    if (validadeContrato.trim() && !pgValidade)
      erros.push("Validade do Contrato inválida. Use DD/MM/AAAA.");

    if (erros.length) {
      setNotice({ type: "error", text: erros.join(" ") });
      return;
    }

    const token = getAccessToken();
    if (!token) {
      setNotice({
        type: "error",
        text: "Token não encontrado no storage (acess_token/access_token/token). Faça login novamente.",
      });
      return;
    }

    const salarioNorm = salario
      ? salario.replace(/\./g, "").replace(",", ".")
      : "";

    const payload: Record<string, any> = {
      // ✅ preferível: backend resolve usuário/empresa pelo token.
      // se quiser compatibilidade temporária, mantém usuario_id quando existir:
      ...(evaUid != null ? { usuario_id: evaUid } : {}),

      nome_completo: nomeCompleto.trim(),
      cpf: cpfDigits,

      // aliases antigos (opcional)
      nome: nomeCompleto.trim(),
      CPF: cpfDigits,
      documento: cpfDigits,

      ...(pgNasc ? { data_de_nascimento: pgNasc } : {}),
      ...(setor.trim() ? { setor: setor.trim() } : {}),
      ...(cargo.trim() ? { cargo: cargo.trim() } : {}),
      ...(salarioNorm ? { salario: salarioNorm } : {}),
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(telefone.trim() ? { telefone: telefone.trim() } : {}),
      ...(contatoEmerg.trim()
        ? { contato_de_emergencia: contatoEmerg.trim() }
        : {}),
      ...(pgValidade ? { validade_do_contrato: pgValidade } : {}),
      ...(upsert ? { upsert: true } : {}),
    };

    const idempotencyKey = `rh-reg-${cpfDigits}`;

    // aborta request anterior (se usuário clicar 2x rápido)
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setSubmitting(true);
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const url = `${API_URL}${upsert ? "?upsert=1&debug=1" : "?debug=1"}`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          Accept: "application/json",
          "x-debug": "1",
          "x-idempotency-key": idempotencyKey,

          // ✅ usa o token com a MESMA lógica do hook de produtos
          Authorization: `Bearer ${token}`,

          // (opcional) mantém headers de UID
          ...buildUidHeaders(),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeout);

      const data: ApiResponse | any = (await safeJson(res)) ?? {
        ok: false,
        message: null,
      };

      if (res.ok && data?.ok) {
        toast.success(
          `Colaborador registrado com sucesso!${
            data?.data?.id ? " ID " + data.data.id : ""
          }`,
          { duration: 4000 }
        );
        clearAll();
        setNotice(null);
        return;
      }

      const msg =
        data?.erro ||
        (Array.isArray(data?.erros) && data.erros.join(" | ")) ||
        data?.detalhe ||
        data?.message ||
        `Falha (${res.status}).`;

      if (res.status === 401)
        setNotice({ type: "error", text: `Sem autenticação (401). ${msg}` });
      else if (res.status === 403)
        setNotice({ type: "error", text: `Sem permissão (403). ${msg}` });
      else if (res.status === 400)
        setNotice({ type: "error", text: `Requisição inválida: ${msg}` });
      else if (res.status === 409)
        setNotice({ type: "error", text: `Conflito: ${msg}` });
      else if (res.status >= 500)
        setNotice({ type: "error", text: `Erro do servidor: ${msg}` });
      else setNotice({ type: "error", text: msg });
    } catch (err: any) {
      clearTimeout(timeout);
      const msg =
        err?.name === "AbortError"
          ? "Tempo de resposta excedido (ou requisição cancelada)."
          : err?.message || "Erro de rede.";
      setNotice({ type: "error", text: msg });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ width: "100%" }}>
      <Toaster position="bottom-center" reverseOrder={false} />
      <TitleButtons title="Departamento Pessoal — Registrar Colaborador" />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        style={{ display: "grid", gap: 10, width: "100%" }}
      >
        {!hasAuthToken ? (
          <AnimatePresence></AnimatePresence>
        ) : (
          <AnimatePresence></AnimatePresence>
        )}

        {evaUid == null ? (
          <AnimatePresence></AnimatePresence>
        ) : (
          <AnimatePresence></AnimatePresence>
        )}

        {/* Obrigatórios */}
        <motion.div variants={itemVariants}>
          <TextField
            label="Nome Completo *"
            value={nomeCompleto}
            onChange={setNomeCompleto}
            placeholder="Ex.: Fernanda Lima"
            style={{ width: "100%" }}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <TextField
            label="CPF *"
            value={cpfView}
            onChange={(v) => setCpfView(maskCPF(v))}
            placeholder="000.000.000-00"
            style={{ width: "100%" }}
          />
        </motion.div>

        {/* Opcionais */}
        <motion.div variants={itemVariants}>
          <TextField
            label="Data de Nascimento (DD/MM/AAAA)"
            value={dataNasc}
            onChange={(v) => setDataNasc(maskBRDate(v))}
            placeholder="Ex.: 22/10/1998"
            style={{ width: "100%" }}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <TextField
            label="Departamento (Setor)"
            value={setor}
            onChange={setSetor}
            placeholder="Ex.: Logística"
            style={{ width: "100%" }}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <TextField
            label="Cargo"
            value={cargo}
            onChange={setCargo}
            placeholder="Ex.: Coordenadora"
            style={{ width: "100%" }}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <TextField
            label="Salário"
            value={salario}
            onChange={setSalario}
            placeholder="Ex.: 7.830,90"
            style={{ width: "100%" }}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <TextField
            label="E-mail"
            value={email}
            onChange={setEmail}
            placeholder="nome.sobrenome@empresa.com"
            style={{ width: "100%" }}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <TextField
            label="Telefone"
            value={telefone}
            onChange={setTelefone}
            placeholder="(51) 94444-8888"
            style={{ width: "100%" }}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <TextField
            label="Contato de Emergência"
            value={contatoEmerg}
            onChange={setContatoEmerg}
            placeholder="Nome - Telefone"
            style={{ width: "100%" }}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <TextField
            label="Validade do Contrato (DD/MM/AAAA)"
            value={validadeContrato}
            onChange={(v) => setValidadeContrato(maskBRDate(v))}
            placeholder="Ex.: 31/12/2026"
            style={{ width: "100%" }}
          />
        </motion.div>

        <AnimatePresence>
          {notice && (
            <NoticeBanner notice={notice} onClose={() => setNotice(null)} />
          )}
        </AnimatePresence>

        {/* Botão principal */}
        <motion.div
          variants={itemVariants}
          style={{ display: "flex", gap: 8, marginTop: 4 }}
        >
          <Button
            text={submitting ? "Enviando..." : "Registrar Colaborador"}
            onClick={handleRegister}
            disabled={submitting || !hasAuthToken}
          />
        </motion.div>
      </motion.div>

      <div style={{ height: 24 }} />
    </div>
  );
};

export default RegistrarColaborador;
